import React, { useRef } from "react"
import Tree, { RenderItemParams, TreeData, TreeDestinationPosition, TreeSourcePosition } from "@atlaskit/tree"
import type { ItemId, TreeItem } from "@atlaskit/tree/types"
import { Category, OnTitleClick } from "../Category/Category"
import "./Catalog.scss"
import { KnowledgeBasePermittedAction } from "../../models/knowledgeBase/permission"
import type { IconProp } from "@fortawesome/fontawesome-svg-core"
import { ArticleKind } from "../../models/article"

export interface CatalogProps {
    tree: TreeData
    onTitleClick: OnTitleClick
    onCanEdit: (permittedAction?: KnowledgeBasePermittedAction) => boolean
    extensions?: JSX.Element[]
    onGetItemExtensions?: (item: TreeItem) => JSX.Element[]
    editingId?: string
    onDragStart?: (itemId: ItemId) => void
    onDragEnd?: (sourcePosition: TreeSourcePosition, destinationPosition?: TreeDestinationPosition) => void
    isDragEnabled?: boolean | ((item: TreeItem) => boolean)
    onExpandCategory?: (itemId: ItemId, onExpand: (itemId: ItemId) => void) => void
    onToggle: (itemId: ItemId, isExpanded: boolean) => void
    onRootItemTitleClick?: OnTitleClick
    categoryIcon?: IconProp | ((item: TreeItem) => IconProp)
    isNestingEnabled?: boolean
    showContentOnHover?: boolean
    availableStatuses?: string[]
    excludeKind?: ArticleKind
    includedArticles?: string[]
    selectedCategoryId?: string
    isTransferDialog?: boolean
    asLink?: boolean
}

const Catalog: React.FC<CatalogProps> = props => {
    const {
        tree,
        extensions,
        onTitleClick,
        onCanEdit,
        onDragEnd,
        onDragStart,
        isDragEnabled,
        onExpandCategory,
        onGetItemExtensions,
        onToggle,
        onRootItemTitleClick,
        categoryIcon,
        editingId,
        showContentOnHover,
        isNestingEnabled = true,
        availableStatuses = [],
        excludeKind,
        includedArticles = [],
        selectedCategoryId,
        isTransferDialog,
        asLink = false
    } = props

    const rootItem = tree.items[tree.rootId]

    const handleCollapse = (itemId: ItemId) => {
        onToggle(itemId, false)
    }

    const handleExpand = (itemId: ItemId) => {
        onToggle(itemId, true)
    }

    const handleExpandCategory = (itemId: ItemId) => {
        if (onExpandCategory) {
            onExpandCategory(itemId, handleExpand)
        } else {
            handleExpand(itemId)
        }
    }

    const moveRef = useRef<string>()

    const renderItem = (params: RenderItemParams) => {
        const { provided, snapshot, item, ...rest } = params

        if (excludeKind === ArticleKind.Scenario && item.data.isScenario) {
            return null
        }

        if (availableStatuses.length && !availableStatuses.includes(item.data.status)) {
            return null
        }

        if (includedArticles.length > 0 && !includedArticles.includes(item.data.symbolCode)) {
            return null
        }

        const children = tree.items[item.children[0]]
        const parent = tree.items[item.data.parentId]
        const noChildren = isTransferDialog && item.children.length === 1 && children && children.data.isDefault
        const isHide = isTransferDialog && item.data.isDefault && parent && parent.children.length === 1

        return (
            <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                <Category
                    {...rest}
                    item={item}
                    onTitleClick={onTitleClick}
                    onCanEdit={onCanEdit}
                    extensions={onGetItemExtensions?.(item)}
                    icon={categoryIcon}
                    selected={editingId === item.id || selectedCategoryId === item.id}
                    showExtensions={editingId === item.id}
                    showContentOnHover={showContentOnHover}
                    isHide={isHide}
                    noChildren={noChildren}
                    asLink={asLink}
                />
            </div>
        )
    }

    return (
        <div className="catalog">
            <Category
                item={rootItem}
                onTitleClick={onRootItemTitleClick}
                onExpand={handleExpand}
                onCanEdit={onCanEdit}
                onCollapse={handleCollapse}
                titleClassName="catalog__title"
                selected={editingId === rootItem.id}
                showExtensions={editingId === rootItem.id}
                extensions={extensions}
                isRoot
            />
            {rootItem.isExpanded && (
                <Tree
                    tree={tree}
                    renderItem={renderItem}
                    onExpand={handleExpandCategory}
                    onCollapse={handleCollapse}
                    onDragStart={onDragStart}
                    onDragEnd={onDragEnd}
                    offsetPerLevel={20}
                    isDragEnabled={isDragEnabled}
                    isNestingEnabled={isNestingEnabled}
                />
            )}
        </div>
    )
}

export default Catalog
